import { useMemo } from "react";

const MAX_VISIBLE_NAMES = 2;

interface Option {
  id?: string | null;
  name?: string | null;
}

/**
 * Generates a display label for a multi-select filter based on selected IDs.
 * Returns "All" if nothing selected, names if few items, or "name1, name2, +N more" format.
 */
export function useSelectionLabel<T extends Option>(
  selectedIds: string[],
  options: (T | null | undefined)[]
): string {
  return useMemo(() => {
    if (selectedIds.length === 0) return "All";

    const selectedNames = selectedIds
      .map((id) => options.find((o) => o?.id === id)?.name)
      .filter((name): name is string => !!name);

    if (selectedNames.length === 0) return `${selectedIds.length} selected`;

    if (selectedNames.length > MAX_VISIBLE_NAMES) {
      const visible = selectedNames.slice(0, MAX_VISIBLE_NAMES);
      const remaining = selectedNames.length - MAX_VISIBLE_NAMES;
      return `${visible.join(", ")}, +${remaining} more`;
    }

    return selectedNames.join(", ");
  }, [selectedIds, options]);
}
